/**
 * Fixed top-right toast region that surfaces incoming push notifications
 * while the user is anywhere in the authenticated app.
 *
 * Each toast:
 *   - Shows the sender avatar + name + push body.
 *   - Auto-dismisses after `pushToastStore.DEFAULT_DURATION_MS`.
 *   - Clicking the body navigates to the resolved deep-link
 *     (e.g. `/notifications` for `NewsCommentMention`).
 *   - Clicking the × dismisses just that toast.
 *
 * Mount once near the app root (e.g. inside `AppShellLayout`). The store is
 * fed by `usePushMessageBridge`.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CloseIcon } from '@/shared/icons';
import Avatar from '@/shared/ui/Avatar';
import { usePushToastStore } from './pushToastStore';

export default function PushToastHost() {
  const { t } = useTranslation('push');
  const toasts = usePushToastStore((s) => s.toasts);
  const dismiss = usePushToastStore((s) => s.dismiss);
  const navigate = useNavigate();

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-6 sm:items-end"
      role="region"
      aria-live="polite"
      aria-label={t('toastRegionAria')}
    >
      {toasts.map((toast) => (
        <button
          key={toast.key}
          type="button"
          onClick={() => {
            dismiss(toast.key);
            if (/^https?:\/\//i.test(toast.clickUrl)) {
              window.location.assign(toast.clickUrl);
            } else if (toast.clickUrl) {
              navigate(toast.clickUrl);
            }
          }}
          className={clsx(
            'pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-lg',
            'transition-all hover:border-gray-300 hover:shadow-xl',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
            'animate-slide-in-right',
          )}
        >
          <div className="flex items-start gap-3 p-3">
            <Avatar src={toast.avatarUrl} initials={toast.initials} size="md" alt={toast.title} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-gray-900">{toast.title}</p>
              {toast.body ? (
                <p className="mt-0.5 line-clamp-2 text-sm text-gray-600">{toast.body}</p>
              ) : null}
            </div>
            <button
              type="button"
              aria-label={t('toastDismissAria')}
              onClick={(event) => {
                event.stopPropagation();
                dismiss(toast.key);
              }}
              className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
        </button>
      ))}
    </div>
  );
}
